import {
  createApiRef,
  type FetchApi,
  type DiscoveryApi,
} from '@backstage/frontend-plugin-api';
import {
  UserInfo,
  VirtualKey,
  ModelInfo,
  UsageMetrics,
  TeamMemberUsage,
  TeamInfo,
  GenerateKeyRequest,
  GenerateKeyResponse,
  UpdateKeyRequest,
  AuditLogsParams,
  PaginatedAuditLogs,
  SpendLogsParams,
  PaginatedSpendLogs,
  LiteLlmConfig,
  QualityResponse,
  VectorStoreInfo,
  McpServerInfo,
  CreateTeamRequest,
  CreateTeamResponse,
  UpdateTeamRequest,
} from './types';
import { profileCacheInstance } from './profileCache';

export class ApiError extends Error {
  body: unknown;
  status: number;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export interface ObjectNames {
  vector_stores: VectorStoreInfo[];
  mcp_servers: McpServerInfo[];
}

export interface LiteLlmApiInterface {
  getUserInfo(): Promise<UserInfo>;
  listKeys(): Promise<VirtualKey[]>;
  generateKey(request: GenerateKeyRequest): Promise<GenerateKeyResponse>;
  updateKey(keyId: string, request: UpdateKeyRequest): Promise<VirtualKey>;
  deleteKey(keyId: string): Promise<{ success: boolean }>;
  blockKey(keyId: string): Promise<void>;
  unblockKey(keyId: string): Promise<void>;
  resetKeySpend(keyId: string): Promise<void>;
  pruneExpiredKeys(): Promise<{
    pruned: number;
    failed: number;
    failures?: { keyId: string; error: string }[];
  }>;
  listModels(): Promise<ModelInfo[]>;
  getTeams(): Promise<TeamInfo[]>;
  getManagedTeams(): Promise<TeamInfo[]>;
  createTeam(request: CreateTeamRequest): Promise<CreateTeamResponse>;
  updateTeam(teamId: string, request: UpdateTeamRequest): Promise<TeamInfo>;
  addTeamMember(
    teamId: string,
    body: { userEntityRef: string; maxBudgetInTeam?: number },
  ): Promise<TeamInfo>;
  removeTeamMember(teamId: string, userEntityRef: string): Promise<TeamInfo>;
  getVectorStores(): Promise<VectorStoreInfo[]>;
  /** Id → name for every KB and MCP server, for labels only (not allowlist-filtered). */
  getObjectNames(): Promise<ObjectNames>;
  setTeamKnowledgeBases(
    teamId: string,
    vectorStores: string[],
  ): Promise<TeamInfo>;
  getMcpServers(): Promise<McpServerInfo[]>;
  setTeamMcpServers(teamId: string, mcpServers: string[]): Promise<TeamInfo>;
  getUsage(startDate: string, endDate: string): Promise<UsageMetrics>;
  getTeamUsage(
    teamId: string,
    startDate: string,
    endDate: string,
  ): Promise<UsageMetrics>;
  /** Per-member breakdown; rejects (403/404) when the caller may not see it. */
  getTeamMemberUsage(
    teamId: string,
    startDate: string,
    endDate: string,
  ): Promise<TeamMemberUsage>;
  getAuditLogs(params: AuditLogsParams): Promise<PaginatedAuditLogs>;
  getSpendLogs(params: SpendLogsParams): Promise<PaginatedSpendLogs>;
  /** Langfuse quality summary; `{configured:false}` when Langfuse is off. */
  getQuality(params: SpendLogsParams): Promise<QualityResponse>;
  /** Mints (or rotates) the caller's connect-flow key, optionally for one team. */
  connectLocalClient(
    redirectUri: string,
    team?: string,
  ): Promise<{ key: string; team: string | null }>;
  getConfig(): Promise<LiteLlmConfig>;
}

export const liteLlmApiRef = createApiRef<LiteLlmApiInterface>({
  id: 'plugin.litellm.api',
});

export type ExpiryStatus = 'expired' | 'soon' | 'ok';

export function expiryStatus(expiresAt?: string): ExpiryStatus | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  const days = diff / (1000 * 60 * 60 * 24);
  if (days < 0) return 'expired';
  if (days < 7) return 'soon';
  return 'ok';
}

export class LiteLlmApi implements LiteLlmApiInterface {
  private fetchApi: FetchApi;
  private base: string | DiscoveryApi;

  /**
   * @param base A DiscoveryApi (the plugin base URL is resolved per request via
   * `discoveryApi.getBaseUrl('litellm')`) or a fixed base URL/path string.
   */
  constructor(
    fetchApi: FetchApi,
    base: string | DiscoveryApi = '/api/litellm',
  ) {
    this.fetchApi = fetchApi;
    this.base = base;
  }

  private async basePath(): Promise<string> {
    return typeof this.base === 'string'
      ? this.base
      : this.base.getBaseUrl('litellm');
  }

  private async throwIfNotOk(response: Response): Promise<void> {
    if (!response.ok) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        body = await response.text().catch(() => '');
      }
      const errorText =
        typeof body === 'object' &&
        body &&
        typeof (body as any).error === 'string'
          ? (body as any).error
          : undefined;
      // Validation failures carry the reason in `details` ("max_budget: must not
      // exceed 100"); without it the user would only see "Invalid request body".
      const details =
        typeof body === 'object' &&
        body &&
        typeof (body as any).details === 'string'
          ? (body as any).details
          : undefined;
      let message = `${response.status} ${response.statusText}`;
      if (errorText) {
        message = details ? `${errorText}: ${details}` : errorText;
      }
      throw new ApiError(message, response.status, body);
    }
  }

  private async get<T>(
    path: string,
    params?: Record<string, string>,
  ): Promise<T> {
    const url = new URL(
      `${await this.basePath()}${path}`,
      window.location.origin,
    );
    if (params) {
      Object.entries(params).forEach(([key, value]) =>
        url.searchParams.append(key, value),
      );
    }
    const response = await this.fetchApi.fetch(url.toString());
    await this.throwIfNotOk(response);
    return response.json();
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const response = await this.fetchApi.fetch(
      `${await this.basePath()}${path}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    await this.throwIfNotOk(response);
    // Any successful write can change the profile (budget, keys, teams).
    profileCacheInstance.invalidateProfile(this);
    return response.json();
  }

  private async del<T>(path: string): Promise<T> {
    const response = await this.fetchApi.fetch(
      `${await this.basePath()}${path}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    await this.throwIfNotOk(response);
    // Any successful write can change the profile (budget, keys, teams).
    profileCacheInstance.invalidateProfile(this);
    return response.json();
  }

  private async send<T>(
    method: 'PATCH' | 'PUT',
    path: string,
    body: unknown,
  ): Promise<T> {
    const response = await this.fetchApi.fetch(
      `${await this.basePath()}${path}`,
      {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    await this.throwIfNotOk(response);
    // Any successful write can change the profile (budget, keys, teams).
    profileCacheInstance.invalidateProfile(this);
    return response.json();
  }

  // User identity is resolved server-side from the Backstage Bearer token.
  // No user_id param needed on the frontend.
  async getUserInfo(): Promise<UserInfo> {
    return this.get<UserInfo>('/user/info');
  }

  async listKeys(): Promise<VirtualKey[]> {
    return this.get<VirtualKey[]>('/keys');
  }

  async generateKey(request: GenerateKeyRequest): Promise<GenerateKeyResponse> {
    return this.post<GenerateKeyResponse>('/keys/generate', request);
  }

  async updateKey(
    keyId: string,
    request: UpdateKeyRequest,
  ): Promise<VirtualKey> {
    return this.post<VirtualKey>(
      `/keys/${encodeURIComponent(keyId)}/update`,
      request,
    );
  }

  async deleteKey(keyId: string): Promise<{ success: boolean }> {
    return this.del<{ success: boolean }>(`/keys/${encodeURIComponent(keyId)}`);
  }

  async blockKey(keyId: string): Promise<void> {
    await this.post(`/keys/${encodeURIComponent(keyId)}/block`, {});
  }

  async unblockKey(keyId: string): Promise<void> {
    await this.post(`/keys/${encodeURIComponent(keyId)}/unblock`, {});
  }

  async resetKeySpend(keyId: string): Promise<void> {
    await this.post(`/keys/${encodeURIComponent(keyId)}/reset_spend`, {});
  }

  async pruneExpiredKeys(): Promise<{
    pruned: number;
    failed: number;
    failures?: { keyId: string; error: string }[];
  }> {
    return this.post<{
      pruned: number;
      failed: number;
      failures?: { keyId: string; error: string }[];
    }>('/keys/prune-expired', {});
  }

  async getAuditLogs(params: AuditLogsParams): Promise<PaginatedAuditLogs> {
    const strParams: Record<string, string> = {};
    if (params.page !== undefined) strParams.page = String(params.page);
    if (params.page_size !== undefined)
      strParams.page_size = String(params.page_size);
    if (params.start_date) strParams.start_date = params.start_date;
    if (params.end_date) strParams.end_date = params.end_date;
    if (params.action) strParams.action = params.action;
    if (params.table_name) strParams.table_name = params.table_name;
    if (params.changed_by) strParams.changed_by = params.changed_by;
    return this.get<PaginatedAuditLogs>('/audit', strParams);
  }

  async listModels(): Promise<ModelInfo[]> {
    return this.get<ModelInfo[]>('/models');
  }

  async getTeams(): Promise<TeamInfo[]> {
    return this.get<TeamInfo[]>('/teams');
  }

  // Teams whose owning_group the caller administers, as opposed to getTeams,
  // which is membership. Scoped server-side; 403 when team management is off.
  async getManagedTeams(): Promise<TeamInfo[]> {
    return this.get<TeamInfo[]>('/teams/managed');
  }

  async createTeam(request: CreateTeamRequest): Promise<CreateTeamResponse> {
    return this.post<CreateTeamResponse>('/teams', request);
  }

  async updateTeam(
    teamId: string,
    request: UpdateTeamRequest,
  ): Promise<TeamInfo> {
    return this.send<TeamInfo>(
      'PATCH',
      `/teams/${encodeURIComponent(teamId)}`,
      request,
    );
  }

  async addTeamMember(
    teamId: string,
    body: { userEntityRef: string; maxBudgetInTeam?: number },
  ): Promise<TeamInfo> {
    return this.post<TeamInfo>(
      `/teams/${encodeURIComponent(teamId)}/members`,
      body,
    );
  }

  // Entity refs carry ':' and '/', so the member goes in a query param.
  async removeTeamMember(
    teamId: string,
    userEntityRef: string,
  ): Promise<TeamInfo> {
    return this.del<TeamInfo>(
      `/teams/${encodeURIComponent(
        teamId,
      )}/members?userEntityRef=${encodeURIComponent(userEntityRef)}`,
    );
  }

  // Already filtered server-side to the operator's allowlist.
  async getVectorStores(): Promise<VectorStoreInfo[]> {
    return this.get<VectorStoreInfo[]>('/vector-stores');
  }

  // Names only, readable by any signed-in user; never used for the picker.
  async getObjectNames(): Promise<ObjectNames> {
    return this.get<ObjectNames>('/object-names');
  }

  async setTeamKnowledgeBases(
    teamId: string,
    vectorStores: string[],
  ): Promise<TeamInfo> {
    return this.send<TeamInfo>(
      'PUT',
      `/teams/${encodeURIComponent(teamId)}/knowledge-bases`,
      { vector_stores: vectorStores },
    );
  }

  async getMcpServers(): Promise<McpServerInfo[]> {
    return this.get<McpServerInfo[]>('/mcp-servers');
  }

  async setTeamMcpServers(
    teamId: string,
    mcpServers: string[],
  ): Promise<TeamInfo> {
    return this.send<TeamInfo>(
      'PUT',
      `/teams/${encodeURIComponent(teamId)}/mcp-servers`,
      { mcp_servers: mcpServers },
    );
  }

  async getUsage(startDate: string, endDate: string): Promise<UsageMetrics> {
    return this.get<UsageMetrics>('/usage', {
      start_date: startDate,
      end_date: endDate,
    });
  }

  async getTeamUsage(
    teamId: string,
    startDate: string,
    endDate: string,
  ): Promise<UsageMetrics> {
    return this.get<UsageMetrics>(
      `/teams/${encodeURIComponent(teamId)}/usage`,
      {
        start_date: startDate,
        end_date: endDate,
      },
    );
  }

  async getTeamMemberUsage(
    teamId: string,
    startDate: string,
    endDate: string,
  ): Promise<TeamMemberUsage> {
    return this.get<TeamMemberUsage>(
      `/teams/${encodeURIComponent(teamId)}/usage/members`,
      { start_date: startDate, end_date: endDate },
    );
  }

  async getSpendLogs(params: SpendLogsParams): Promise<PaginatedSpendLogs> {
    const strParams: Record<string, string> = {
      start_date: params.startDate,
      end_date: params.endDate,
    };
    if (params.userId) strParams.userId = params.userId;
    if (params.teamId) strParams.teamId = params.teamId;
    if (params.page !== undefined) strParams.page = String(params.page);
    if (params.pageSize !== undefined)
      strParams.page_size = String(params.pageSize);
    return this.get<PaginatedSpendLogs>('/insights/spend-logs', strParams);
  }

  async getQuality(params: SpendLogsParams): Promise<QualityResponse> {
    const strParams: Record<string, string> = {
      start_date: params.startDate,
      end_date: params.endDate,
    };
    if (params.userId) strParams.userId = params.userId;
    return this.get<QualityResponse>('/insights/quality', strParams);
  }

  // JSON body, so the backend answers `{ key, team }` instead of redirecting.
  async connectLocalClient(
    redirectUri: string,
    team?: string,
  ): Promise<{ key: string; team: string | null }> {
    return this.post('/opencode/connect', { redirect_uri: redirectUri, team });
  }

  async getConfig(): Promise<LiteLlmConfig> {
    return this.get<LiteLlmConfig>('/config');
  }
}
